import { defineCollection } from "@rebasepro/common";

/**
 * One row per buyer question asked of one model, on one day.
 *
 * ── Why this table exists ────────────────────────────────────────────────────
 * Every other collection in this app counts things *leaving* the pipeline —
 * signals found, prospects enriched, emails sent, replies received. All of it
 * is outbound, and all of it is bounded by how many strangers one person can
 * write to in a week.
 *
 * This is the first inbound measurement. It answers the only question that
 * scales: **when a developer asks an AI for a tool like rebase, does rebase
 * come up?** In 2026 that question decides more evaluations than a search
 * ranking does, and until this table existed the app could not answer it at
 * all — not "the answer is no", but "there is no instrument".
 *
 * ── What a row is, and what it is not ────────────────────────────────────────
 * A row is a measurement, not a judgement. The prompt is asked exactly as a
 * buyer would ask it, with **nothing in it that mentions rebase** — the moment
 * the question names the product, the answer stops being evidence and becomes a
 * prompt completion. `mentioned` is then decided by reading the answer here,
 * never by asking the model to grade itself.
 *
 * The full `answer` is stored verbatim, and it is the most useful column on the
 * table: a position of 4 tells you where you are, and the sentence around the
 * mention tells you *what the model thinks rebase is*, which is the thing you
 * can actually go and fix.
 *
 * ── Why the rows are kept forever ────────────────────────────────────────────
 * A single check is nearly worthless; the series is the product. "Not mentioned"
 * on one Tuesday is noise, and "not mentioned for eleven weeks, while Directus
 * moved from 6th to 2nd" is a strategy meeting. So nothing here is updated —
 * every run appends, the same rule `activities` follows, and for the same
 * reason: a table that overwrites cannot answer a question about change.
 */
const visibilityChecksCollection = defineCollection({
    name: "AI visibility",
    singularName: "Visibility check",
    slug: "visibility_checks",
    table: "visibility_checks",
    description:
        "What an AI says when a buyer asks about this category, and whether rebase is in the answer. "
        + "One row per question per run, kept forever — the trend is the point, not any single row.",
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        prompt: {
            name: "Question",
            type: "string",
            validation: { required: true },
            description: "Asked exactly as a buyer would ask it. Never mentions rebase — a question that names the product measures nothing."
        },
        model: {
            name: "Model",
            type: "string",
            validation: { required: true },
            description: "Which model answered, at which version. Comparing across models is meaningless; comparing one model to itself over time is the measurement."
        },
        grounded: {
            name: "Web-grounded",
            type: "boolean",
            defaultValue: false,
            description: "Whether the answer was backed by a live web search. An ungrounded answer reflects training data — months stale, and unmovable by anything you publish this quarter."
        },
        mentioned: {
            name: "rebase named",
            type: "boolean",
            defaultValue: false,
            description: "Whether rebase appears in the answer as a product. 'git rebase' does not count — see mentionsRebase() for why that had to be said out loud."
        },
        position: {
            name: "Position",
            type: "number",
            description: "Where rebase falls in the answer's running order of named products. 1 is first mentioned, not 'best'. Empty when it is absent."
        },
        rivals: {
            name: "Who was named",
            type: "array",
            columnType: "text[]",
            of: { name: "Product", type: "string" },
            description: "Every recognised product in the answer, in the order the answer named them. This is the leaderboard."
        },
        rival_count: {
            name: "Products named",
            type: "number",
            description: "Length of the list above, stored so the board can sort and the brief can count without reading arrays."
        },
        answer: {
            name: "Answer",
            type: "string",
            admin: { markdown: true },
            description: "The reply, verbatim. The most useful column here: it says what the model thinks rebase is, which is the part you can go and change."
        },
        citation_count: {
            name: "Sources cited",
            type: "number",
            defaultValue: 0,
            description: "How many web sources the answer leaned on. Each becomes a row in Citation targets."
        },
        asked_at: {
            name: "Asked at",
            type: "date",
            autoValue: "on_create",
            admin: { readOnly: true }
        }
    },
    admin: {
        icon: "Visibility",
        group: "Visibility",
        defaultViewMode: "table",
        enabledViews: ["table", "list"],
        display: {
            title: "prompt"
        },
        sort: ["asked_at", "desc"],
        propertiesOrder: [
            "prompt",
            "mentioned",
            "position",
            "rivals",
            "rival_count",
            "model",
            "grounded",
            "citation_count",
            "answer",
            "asked_at"
        ],
        filterPresets: [
            {
                label: "Where we are absent",
                filterValues: { mentioned: ["==", false] }
            },
            {
                label: "Where we are named",
                filterValues: { mentioned: ["==", true] }
            },
            {
                label: "Ungrounded (training data only)",
                filterValues: { grounded: ["==", false] }
            }
        ]
    }
});

export default visibilityChecksCollection;
